import React, { useEffect, useRef } from 'react';
import './styles/CompanyPurgeConfirmDialog.css';

const CompanyPurgeConfirmDialog = ({
  open,
  company,
  busy,
  confirmName = '',
  onConfirmNameChange,
  onCancel,
  onConfirm,
  error = '',
}) => {
  const inputRef = useRef(null);
  const cancelRef = useRef(null);

  const companyName = company?.name || '';
  const nameMatches = confirmName.trim() === companyName.trim();
  const canConfirm = nameMatches && !busy;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    inputRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) {
        onCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, busy, onCancel]);

  if (!open || !company?.id) {
    return null;
  }

  return (
    <div
      className="company-purge-dialog__backdrop"
      onClick={busy ? undefined : onCancel}
      role="presentation"
    >
      <div
        className="company-purge-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="company-purge-dialog-title"
        aria-describedby="company-purge-dialog-description"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="company-purge-dialog__icon" aria-hidden="true">
          !
        </div>

        <h2 id="company-purge-dialog-title" className="company-purge-dialog__title">
          Are you sure you want to delete this company?
        </h2>

        <p id="company-purge-dialog-description" className="company-purge-dialog__lead">
          You are about to permanently delete <strong>{company.name || 'this company'}</strong>.
          This action cannot be undone.
        </p>

        <div className="company-purge-dialog__company-card">
          <div><strong>Company:</strong> {company.name || '—'}</div>
          <div><strong>Company ID:</strong> {company.id}</div>
          {company.email ? <div><strong>Email:</strong> {company.email}</div> : null}
        </div>

        <div className="company-purge-dialog__warning">
          <p className="company-purge-dialog__warning-title">This will permanently remove:</p>
          <ul className="company-purge-dialog__list">
            <li>The company and related Postgres records (clients, appointments, employees, settings, etc.)</li>
            <li>Subscriptions, SMS logs, and invites linked to this company</li>
            <li>App user rows for this company in Postgres</li>
            <li>Sign-in accounts and storage files stay until a later cutover</li>
          </ul>
        </div>

        <label className="company-purge-dialog__label" htmlFor="company-purge-dialog-confirm-name">
          Type <strong>{companyName}</strong> to confirm
        </label>
        <input
          ref={inputRef}
          id="company-purge-dialog-confirm-name"
          className="company-purge-dialog__input"
          type="text"
          value={confirmName}
          onChange={(event) => onConfirmNameChange?.(event.target.value)}
          placeholder={companyName}
          disabled={busy}
          autoComplete="off"
        />

        {error ? (
          <div className="company-purge-dialog__alert" role="alert">
            {error}
          </div>
        ) : null}

        <div className="company-purge-dialog__actions">
          <button
            ref={cancelRef}
            type="button"
            className="at-btn company-purge-dialog__btn-cancel"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="at-btn company-purge-dialog__btn-confirm"
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            {busy ? 'Deleting…' : 'Yes, permanently delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyPurgeConfirmDialog;
